import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';

const NAV_LINKS = [
  { path: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
  { path: '/utenti', label: 'Utenti e abilità', icon: PeopleIcon },
  { path: '/le-mie-richieste', label: 'Le mie richieste', icon: SwapHorizIcon },
  { path: '/profile', label: 'Profilo', icon: PersonIcon },
  { path: '/profile/skills', label: 'Gestione abilità', icon: StarIcon },
  { path: '/registra-scambio', label: 'Registra scambio', icon: AddCircleIcon },
  { path: '/account', label: 'Saldo', icon: AccountBalanceWalletIcon },
  { path: '/regolamento', label: 'Regolamento', icon: GavelIcon },
];

const ADMIN_LINKS = [
  { path: '/admin/users', label: 'Utenti', icon: ManageAccountsIcon },
  { path: '/admin/categories', label: 'Categorie', icon: CategoryIcon },
  { path: '/admin/requests', label: 'Richieste', icon: AssignmentIcon },
  { path: '/admin/exchange-requests', label: 'Richieste scambio', icon: CompareArrowsIcon },
];

function NavLink({ path, label, icon: Icon, active, onClick }) {
  return (
    <Link
      to={path}
      onClick={onClick}
      className={[
        'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
        active
          ? 'bg-slate-900 text-white shadow-sm'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
      ].join(' ')}
    >
      <Icon sx={{ fontSize: 18 }} />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function SidebarNav({ activePath, isAdmin, currentUser, logout, onNavClick }) {
  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
        {NAV_LINKS.map(({ path, label, icon }) => (
          <NavLink
            key={path}
            path={path}
            label={label}
            icon={icon}
            active={activePath === path}
            onClick={onNavClick}
          />
        ))}

        {isAdmin && (
          <>
            <div className="px-3 pb-1.5 pt-5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Amministrazione
              </span>
            </div>
            {ADMIN_LINKS.map(({ path, label, icon }) => (
              <NavLink
                key={path}
                path={path}
                label={label}
                icon={icon}
                active={activePath === path}
                onClick={onNavClick}
              />
            ))}
          </>
        )}
      </nav>

      <div className="shrink-0 border-t border-slate-200 p-3">
        {currentUser && (
          <div className="mb-1 px-3 py-2">
            <div className="truncate text-sm font-semibold text-slate-900">
              {currentUser.first_name
                ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
                : currentUser.username}
            </div>
            <div className="truncate text-xs text-slate-500">{currentUser.email}</div>
          </div>
        )}
        <button
          type="button"
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-700"
        >
          <LogoutIcon sx={{ fontSize: 18 }} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}

const Header = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const authPaths = ['/login', '/register', '/change-password'];
  if (authPaths.includes(location.pathname)) return null;

  const isAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'moderator' ||
    currentUser?.role === 'superadmin';

  const navProps = { activePath: location.pathname, isAdmin, currentUser, logout };

  return (
    <>
      {/* ── Desktop sidebar ─────────────────────────────────── */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white shadow-sm md:flex">
        <div className="flex shrink-0 items-center gap-2.5 border-b border-slate-200 px-4 py-4">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img
              src="/logogcomune.png"
              alt="Comune di Poggioreale"
              className="h-8 w-8 shrink-0 rounded-lg object-contain"
            />
            <div className="min-w-0">
              <div className="truncate text-xs font-bold leading-tight text-slate-900">
                Banche del Tempo
              </div>
              <div className="truncate text-[10px] leading-tight text-slate-400">
                Comune di Poggioreale
              </div>
            </div>
          </Link>
        </div>
        <SidebarNav {...navProps} />
      </aside>

      {/* ── Mobile top bar ──────────────────────────────────── */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur md:hidden">
        <button
          type="button"
          aria-label="Apri menu"
          onClick={() => setMobileOpen(true)}
          className="btn-ghost -ml-1 p-2"
        >
          <MenuIcon fontSize="small" />
        </button>
        <Link to="/dashboard" className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-bold text-slate-900">Banche del Tempo</span>
        </Link>
      </header>

      {/* ── Mobile drawer overlay ───────────────────────────── */}
      <div
        className={[
          'fixed inset-0 z-50 transition-opacity duration-200 md:hidden',
          mobileOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
        aria-hidden={!mobileOpen}
      >
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
        <aside
          className={[
            'absolute inset-y-0 left-0 flex w-72 flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          ].join(' ')}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 px-4">
            <div className="flex items-center gap-2">
              <img
                src="/logogcomune.png"
                alt="Comune di Poggioreale"
                className="h-7 w-7 rounded-lg object-contain"
              />
              <span className="text-sm font-bold text-slate-900">Banche del Tempo</span>
            </div>
            <button
              type="button"
              aria-label="Chiudi menu"
              onClick={() => setMobileOpen(false)}
              className="btn-ghost p-1.5"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <SidebarNav {...navProps} onNavClick={() => setMobileOpen(false)} />
        </aside>
      </div>
    </>
  );
};

export default Header;
